import { CardTitle } from '@ibsys/kit';
import { type FC, Fragment, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ConversationInfoResponse } from '@/entities/conversations';
import { getAttrValueByPath, getConversationAttrText } from '@/entities/conversations/model/utils';
import { formatDateTime } from '@/shared/lib';

/** Атрибут с тематикой обращения */
const TOPIC_ATTRIBUTE_PATH = 'taskName';

type Props = {
  /** Карточка диалога; пока не получена — только заголовок */
  conversation?: ConversationInfoResponse;
  /**
   * Действие справа от заголовка. Текст шапки его обтекает: в узкой панели даты и тематика,
   * не уместившиеся рядом, уходят под него и занимают всю ширину шапки.
   */
  action?: ReactNode;
};

/**
 * Шапка переписки: «Диалог» и даты начала и завершения коммуникации (без завершения — только
 * начало), ниже жирным — тематика обращения, если она известна. Тематика переносится,
 * а не сокращается многоточием.
 */
export const DialogTextHeader: FC<Props> = ({ conversation, action }) => {
  const { t } = useTranslation(['dialogs']);

  const startedAt = formatDateTime(conversation?.createdAt);
  const finishedAt = formatDateTime(conversation?.processedAt);
  // Каждая дата неразрывна, если ей хватает ширины шапки: диапазон переносится между датами
  const dateParts = startedAt && finishedAt ? [`${startedAt} —`, finishedAt] : [startedAt];
  const topic = conversation
    ? getConversationAttrText(getAttrValueByPath(conversation.conversationAttrValues, TOPIC_ATTRIBUTE_PATH))
    : null;

  return (
    <div className="min-w-0 flex-1">
      {/* Обёртка ровно по высоте действия: строчная была бы выше него и сужала бы строку дат под ним */}
      {action && <div className="float-right ms-4 flex">{action}</div>}
      <div>
        <CardTitle className="me-1 inline">{t('dialogs:dialog')}</CardTitle>{' '}
        {startedAt && (
          <span className="text-sm text-muted-foreground">
            {dateParts.map((part, index) => (
              <Fragment key={part}>
                {index > 0 && ' '}
                <span className="inline-block">{part}</span>
              </Fragment>
            ))}
          </span>
        )}
      </div>
      {topic && <div className="mt-0.5 text-sm font-bold wrap-break-word">{topic}</div>}
    </div>
  );
};
